import { registerCommand } from "./registry.ts";
import type { CommandContext } from "./registry.ts";
import { getCurrentSID } from "../plugins/addressing.ts";
import { parseViewspecCodes } from "../../viewspecs/codes.ts";

/**
 * Scroll to a statement by SID in the editor view.
 */
function scrollToSID(ctx: CommandContext, sid: string): void {
  const view = ctx.editorView;
  if (!view) return;

  let targetPos: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (
      node.type.name === "statement" &&
      (node.attrs.sid as string) === sid
    ) {
      targetPos = pos;
      return false;
    }
  });

  if (targetPos !== null) {
    const dom = view.domAtPos(targetPos + 1);
    if (dom.node) {
      const el =
        dom.node instanceof HTMLElement
          ? dom.node
          : dom.node.parentElement;
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
}

// --- Jump ---
registerCommand({
  name: "jump",
  aliases: ["j", "ji"],
  description: "Jump to a statement by SID",
  args: [
    { name: "target", type: "sid", prompt: "Jump to:" },
    { name: "viewspec", type: "viewspec", prompt: "V:", optional: true },
  ],
  execute(ctx, args) {
    if (args.target) {
      scrollToSID(ctx, args.target);
    }
    if (args.viewspec) {
      ctx.setViewspecs(parseViewspecCodes(args.viewspec));
    }
  },
});

// --- Goto ---
registerCommand({
  name: "goto",
  aliases: ["g", "gd"],
  description: "Go to a different document",
  args: [{ name: "document", type: "document", prompt: "Document:" }],
  execute(ctx, args) {
    if (args.document) {
      ctx.navigate(`/doc/${args.document}`);
    }
  },
});

// --- Back ---
registerCommand({
  name: "back",
  aliases: ["b"],
  description: "Go back to previous location",
  args: [],
  execute() {
    history.back();
  },
});

// --- Forward ---
registerCommand({
  name: "forward",
  aliases: ["fw"],
  description: "Go forward",
  args: [],
  execute() {
    history.forward();
  },
});

// --- Follow ---
registerCommand({
  name: "follow",
  aliases: ["fl"],
  description: "Follow the link at cursor position",
  args: [],
  execute(ctx) {
    const view = ctx.editorView;
    if (!view) return;

    const { $from } = view.state.selection;
    const marks = $from.marks();
    const linkMark = marks.find((m) => m.type.name === "nls_link");

    if (linkMark) {
      const docId = linkMark.attrs.documentId as string;
      const sid = linkMark.attrs.sid as string | undefined;
      const viewspec = linkMark.attrs.viewspec as string | undefined;

      if (docId && docId !== ctx.currentDocument?.id) {
        const path = sid ? `/doc/${docId}#${sid}` : `/doc/${docId}`;
        ctx.navigate(path);
      } else if (sid) {
        scrollToSID(ctx, sid);
      }

      if (viewspec) {
        ctx.setViewspecs(parseViewspecCodes(viewspec));
      }
    }
  },
});
